<template>
	<div id="modal" v-if="showModal" @keydown.esc="closeModal" tabindex="-1" ref="modal">
		<div :class="globalStore.modal.bigMode ? 'big':''" class="modalWrapper">
			<div class="title">{{ modalTitle }}</div>
			<div class="popupContent" >
				<!--  Variable content -->
				<div>
					<div class="w-full popupInputs" id="popupWrapper">

						<div v-if="loading" class="text-center">
							<LoadingSpinner/>
						</div>
						<div v-else>
              <div v-if="modalType === PopupType.fileEdit">
                <FileEditPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.fileDetails">
                <FileDetailPopup :data="dataCopy"/>
              </div>
							<div v-else-if="modalType === PopupType.ticketEdit">
								<TicketEditPopup :data="dataCopy"/>
							</div>
              <div v-else-if="modalType === PopupType.ticketDetails">
                <TicketDetailPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.recipientEdit">
                <RecipientEditPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.recipientDetails">
                <RecipientDetailsPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.recipientlistEdit">
                <RecipientlistEditPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.recipientlistDetails">
                <RecipientlistDetailsPopup :data="dataCopy"/>
              </div>
              <div v-else-if="modalType === PopupType.genericText">
                <GenericTextPopup :data="dataCopy"/>
              </div>
							<div v-else>
								{{ PopupType[PopupType[modalType]] }} Popup
							</div>
						</div>
					</div>
				</div>



			</div>

			<div class="footer flex">
        <div class="text-left">
          <button class="primary mr-2" id="okButton" @click="confirmModal()" v-if="popupNeedsCallbackButton()" :disabled="loadingConfirm">
            <TextOrSpinner :text="dynamicButtonText" :spin="loadingConfirm"/>
          </button>
<!--          <button class="primary" @click="confirmModal()" v-else-if="isFilterPopup()" :disabled="loadingConfirm">
            <TextOrSpinner :text="`${lengthAfterFilter} ${t('general.results')}`" :spin="loadingConfirm"/>
          </button> TODO this comes later-->
          <button class="btn secondary" @click="closeModal()" v-if="props.showCancelButton">{{t('general.cancel')}}</button>
				</div>

        <div class="flex-1">
          <div class="floatingError ml-4 mr-1">
            <Error :errorLoc="ErrorLocationType.popup"/>
          </div>
          <button class="btn secondary" v-if="resetButtonState" @click="resetPreferences()">{{t('general.reset')}}<span class="icon-reset ml-1"></span></button>
        </div>

			</div>
		</div>

	</div>
</template>

<script setup>
import {computed, defineProps, ref, watchEffect} from 'vue';
import { useGlobalStore } from '@/stores/GlobalStore'
import {ErrorType, FileStatus, PopupType, TicketType} from "@/utilities/enums/Enums";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Error from "@/components/Error.vue";
import { ErrorLocationType } from "@/utilities/enums/Enums";
import {useI18n} from "vue-i18n";
import TextOrSpinner from "@/components/TextOrSpinner";
import FileEditPopup from "@/components/popups/FileEditPopup";
import TicketEditPopup from "@/components/popups/TicketEditPopup.vue";
import RecipientEditPopup from "@/components/popups/RecipientEditPopup.vue";
import RecipientlistEditPopup from "@/components/popups/RecipientlistEditPopup.vue";
import RecipientlistDetailsPopup from "@/components/popups/RecipientlistDetailsPopup.vue";
import RecipientDetailsPopup from "@/components/popups/RecipientDetailsPopup.vue";
import FileDetailPopup from "@/components/popups/FileDetailPopup.vue";
import TicketDetailPopup from "@/components/popups/TicketDetailPopup.vue";
import GenericTextPopup from "@/components/popups/GenericTextPopup.vue";
import {useErrorStore} from "@/stores/ErrorStore";

const { t } = useI18n();

const props = defineProps({
	showCancelButton: {
		type: Boolean,
		default: true
	},
	showResetButton: {
		type: Boolean,
		default: true
	},
	data: Object
});

const loading = ref(false);
const loadingConfirm = ref(false);
const globalStore = useGlobalStore();

// Make a REF Copy of the Data
const dataCopy = ref(null);

// make flat copy of props.data by hand, otherwise v-model pushes changes from the popup through to the record data in the grid, which is buggy and weird (grid data changes but db-data doesnt)
watchEffect(()=>{
  const copy = {};
  for (const keyValueArray of Object.entries(props.data)){
    copy[keyValueArray[0]] = keyValueArray[1];
  }
  dataCopy.value = copy;
})

const dynamicButtonText = computed(()=>{
  const needsToGenerateTickets = dataCopy.value.recipients?.some(()=>true)
      || dataCopy.value.recipientLists?.some(()=>true)
      || (dataCopy.value.newRecipient?.firstName
          && dataCopy.value.newRecipient?.lastName
          && dataCopy.value.newRecipient?.email)

  const showSendText = needsToGenerateTickets && dataCopy.value.status === FileStatus.completed;

  if(modalType.value === PopupType.ticketEdit){
    return dataCopy.value.id !== null
        ? needsToGenerateTickets
            ? t('dashboard.tabs.ticket.popups.edit.sendTicket')
            : t('general.save')
        : dataCopy.value.type === TicketType.staticDownload
            ?  t('dashboard.tabs.ticket.popups.edit.generateLink')
            : t('dashboard.tabs.ticket.popups.edit.sendTicket')
  }

  if(modalType.value === PopupType.fileEdit){
    return needsToGenerateTickets && !dataCopy.value.status === FileStatus.completed
        ? t('dashboard.tabs.file.popups.edit.buttonText.uploadFileAndSendTicket')
        : dataCopy.value.status === FileStatus.completed
            ? t('general.confirm')
            : t('dashboard.tabs.file.popups.edit.buttonText.uploadFile')
  }

  return showSendText
      ? t('dashboard.tabs.ticket.popups.edit.sendTicket')
      : t('general.confirm');
});

const popupNeedsCallbackButton = ()=>{
  return ![
      PopupType.recipientlistDetails,
      PopupType.recipientDetails,
      PopupType.fileDetails,
      PopupType.ticketDetails,
      PopupType.genericText
  ]
      .includes(modalType.value)
}

const showModal = computed(() => {
	return globalStore.modal.show;
});

const modalTitle = computed(() => {
	return globalStore.modal.title;
});

const modalType = computed(() => {
	return globalStore.modal.popupType;
});

const resetButtonState = computed(() => {
	return globalStore.modal.resetButton;
});


const closeModal = async () => {
  globalStore.closeModal();
};

const confirmModal = async () => {
  loadingConfirm.value = true;

  // update contentObject before proceeding with save action (not done automatically since it is flatCopy)
  globalStore.modal.contentObject = dataCopy.value;

	const callback = globalStore.modal.action;
	const isValid = await globalStore.modal.validator.$validate();

  try{
    // check if callback action is a function and run validation
    if(typeof(callback) === "function" && isValid) await callback(); // closing should be done via the callback
    else {
      const errorStore = useErrorStore();
      errorStore.showError(t("general.validation.failed"), ErrorType.error, ErrorLocationType.popup)

      const targetElement  = document.querySelector(".error");
      targetElement.scrollIntoView({behavior: "smooth", block: 'center' });
    }
  }
  catch (ex) {
    console.log("error when callback action", ex)
  }
  finally {
    loadingConfirm.value = false;
  }
};

const resetPreferences = () => { // TODO this comes later
	// const filteredDefaultHiddenCols = globalStore.settings.defaultHiddenColumns.filter((dhc)=>{
	// 	switch(modalType.value){
	// 		case PopupType.formReportFilterPopup:
  //       return dhc.tableName === "form";
	// 		default:
	// 			console.log("PopupType not know when resetting filters");
	// 			return false;
	// 	}
	// });
  // globalStore.setTmpFilters([]);
  // globalStore.setTmpHiddenColumns(filteredDefaultHiddenCols);
  // globalStore.setCountAfterFilters(globalStore.gridStore.storage.allValues.length);
};

</script>

<style lang="scss">
	#modal {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100vh;
		position: fixed;
		top: 0;
		z-index: 4999;
		background: rgba(0,0,0,0.5);
		flex-flow: column;
		.modalWrapper {
			background: $white;
			width: 800px;
		}
		.title {
			@apply w-full  pb-2 mb-3 pl-8 pt-9 text-left py-3;
      font-size: 24px;
      font-weight: bold;
			color: $ogeTextBlue;
			background: $white;
		}
		.inputRow {
			@apply my-3 p-1;
			display: flex;
			flex-direction: column;
			flex-basis: 100%;
			flex: 1;
		}
		.groupTitle {
			@apply mb-4;
		}
		.groupInputs {
			flex-direction: row;
			flex-wrap: wrap;
			column-gap: 20px;
			width: 100%;
		}
		.inputGroup {
			width: 100%;
			@apply mb-10 pb-10;
			border-bottom: 1px solid $foggyGrey;
		}
		.popupContent {
      @apply pl-8 pr-8;
			.popupInputs {
				@apply pr-4;
				max-height: 60vh;
				overflow-y: auto;
			}
		}
		.footer {
			@apply pt-2 mt-3 pl-8 pb-9;
			background: $white;
		}
		.floatingError {
			//position: absolute;
			//width: calc(100% - 65px);
			//bottom: 0px;
		}
	}
  .big {
    width: 1000px !important;
  }
</style>
